import React, { useEffect, useState, useMemo } from "react";
import "../css/dropzone.css"

import {
    Grid,
    FormControl,
    TextField,
    Button,
    Select,
    MenuItem,
    Chip,
    Box,
    IconButton,
    Input,
    FormHelperText,
    CircularProgress,
    Snackbar,
    Alert
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { authorizedFetch } from "../utils/authorizedFetch";
import { useAuth } from "../utils/useAuth";

export default function EventForm({ selectedEvent, displayTypes = [], onSuccess }) {
    const { getAccessToken } = useAuth();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});
    const [imageData, setImageData] = useState(null);
    const [deleteImage, setDeleteImage] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const showSnackbar = (severity, message) => setSnackbar({ open: true, severity, message });

    const handleCloseSnackbar = (_, reason) => {
        if (reason === "clickaway") return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const normalizeDisplayType = (displayType) => ({
        id: String(displayType.id ?? displayType),
        name: displayType.name ?? String(displayType.id ?? displayType)
    });

    const getInitialState = (data = null) => ({
        id: data?.id || "",
        name: data?.name || "",
        areas: data?.areas?.length
            ? data.areas.map(a => ({ ...a }))
            : [{ id: "", name: "" }],
        displayTypes: data?.displayTypes?.length
            ? data.displayTypes.map(d => String(d.id ?? d))
            : []
    });

    useEffect(() => {
        setFormData(getInitialState(selectedEvent));
        setErrors({});
        setImageData(null);
        setLoading(false);
        setDeleteImage(false);
    }, [selectedEvent]);

    const displayTypeOptions = useMemo(() => {
        const map = new Map();

        displayTypes.forEach((dt) => {
            const n = normalizeDisplayType(dt);
            map.set(n.id, n);
        });

        selectedEvent?.displayTypes?.forEach((dt) => {
            const n = normalizeDisplayType(dt);
            map.set(n.id, n);
        });

        return Array.from(map.values());
    }, [displayTypes, selectedEvent]);

    const displayTypeMap = useMemo(() => {
        return new Map(displayTypeOptions.map(d => [d.id, d.name]));
    }, [displayTypeOptions]);

    const validate = () => {
        const newErrors = {};

        if (!formData?.name?.trim()) {
            newErrors.name = "Name is required";
        }

        const areaErrors = formData?.areas?.map(a => !a.name?.trim());

        if (areaErrors?.some(Boolean)) {
            newErrors.areas = areaErrors;
        }

        if (!formData?.displayTypes?.length) {
            newErrors.displayTypes = "Select at least one display type";
        }

        return newErrors;
    };

    const handleRemoveImage = () => {
        setImageData(null);
        setDeleteImage(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            const accessToken = await getAccessToken();

            const payload = {
                id: formData.id || null,
                name: formData.name,
                areas: formData.areas,
                width: imageData?.width ?? null,
                height: imageData?.height ?? null,
                uploadImage: !!imageData?.file,
                deleteImage,
                displayTypes: formData.displayTypes.map((id) => ({
                    id,
                    name: displayTypeMap.get(String(id)) || id
                }))
            };

            const result = await authorizedFetch(
                process.env.REACT_APP_API_ENDPOINT + "events",
                {
                    method: selectedEvent ? "PUT" : "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                },
                accessToken
            );

            let finalResult = result;

            if (imageData?.file && result?.uploadUrl) {

                const uploadResponse = await fetch(result.uploadUrl, {
                    method: "PUT",
                    headers: {
                        "Content-Type": imageData.file.type
                    },
                    body: imageData.file
                });

                if (!uploadResponse.ok) {
                    throw new Error("S3 image upload failed");
                }

                finalResult = {
                    ...result,
                    imageUploaded: true
                };
            }

            onSuccess?.(finalResult);

            showSnackbar("success", selectedEvent ? "Event updated successfully" : "Event created successfully");

        } catch (err) {
            console.error(err);
            showSnackbar("error", err?.data?.message || "Error while saving the event");

            if (selectedEvent) {
                setFormData(getInitialState(selectedEvent));
                setImageData(null);
                setDeleteImage(false);
                setErrors({});
            }
        } finally {
            setLoading(false);
        }
    };

    const hasImage = imageData || (selectedEvent?.imageUrl && !deleteImage);

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            setImageData(null);
            return;
        }

        const img = new Image();

        img.onload = () => {
            setImageData({
                file,
                width: img.width,
                height: img.height
            });

            setDeleteImage(false);

            URL.revokeObjectURL(img.src);
        };

        img.src = URL.createObjectURL(file);
    };

    if (!formData) return null;

    const handleImageDrop = (e) => {
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleImageChange({ target: { files: [file] } });
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
        >
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        className="gucci_text"
                        name="name"
                        label="Name"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData(prev => ({...prev, name: e.target.value}))
                        }
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        className="gucci_text"
                        name="id"
                        label="ID"
                        disabled
                        value={formData.id}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box mb={2}>
                        <strong className="gucci_text">Areas</strong>
                    </Box>

                    <Grid container spacing={2}>
                        {formData.areas.map((area, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Box display="flex" gap={1} alignItems="center">
                                    <TextField
                                        fullWidth
                                        label={`Area ${index + 1}`}
                                        value={area.name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFormData(prev => ({
                                                ...prev,
                                                areas: prev.areas.map((a, i) => i === index ? { ...a, name: value } : a)
                                            }));
                                        }}
                                        error={!!errors.areas?.[index]}
                                        helperText={
                                            errors.areas?.[index]
                                                ? "Area name is required"
                                                : ""
                                        }
                                    />

                                    <IconButton
                                        onClick={() =>
                                            setFormData(prev => ({
                                                ...prev,
                                                areas: [...prev.areas, { id: "", name: "" }]
                                            }))
                                        }
                                    >
                                        <AddIcon />
                                    </IconButton>

                                    {formData.areas.length > 1 && (
                                        <IconButton
                                            onClick={() => {
                                                const updated = [...formData.areas];
                                                updated.splice(index, 1);
                                                setFormData(prev => ({ ...prev, areas: updated }));
                                            }}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Box>
                        <strong className="gucci_text">Display Types</strong>
                    </Box>

                    <FormControl fullWidth error={!!errors.displayTypes}>
                        <Select
                            className='gucci_text select_filter'
                            multiple
                            value={formData.displayTypes}
                            onChange={(e) => {
                                const value = typeof e.target.value === "string"
                                    ? e.target.value.split(",")
                                    : e.target.value;

                                setFormData(prev => ({
                                    ...prev,
                                    displayTypes: value
                                }));
                            }}
                            input={<Input />}
                            renderValue={(selectedIds) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selectedIds.map((id) => (
                                        <Chip
                                            key={id}
                                            label={displayTypeMap.get(String(id)) || id}
                                        />
                                    ))}
                                </Box>
                            )}
                        >
                            {displayTypeOptions.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>

                        {errors.displayTypes && (
                            <FormHelperText>
                                {errors.displayTypes}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Box mb={1}>
                        <strong className="gucci_text">Map</strong>
                    </Box>

                    <div
                        className="dropzone"
                        onClick={() => document.getElementById("fileInput").click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            handleImageDrop(e);
                        }}
                    >
                        <Button
                            className="gucci_text"
                            variant="outlined"
                            component="span"
                            disabled={loading}
                        >
                            Select image or drop here
                        </Button>

                        <input
                            id="fileInput"
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                    </div>
                    {imageData && (
                        <Box className="flex center" mt={1}>
                            <img
                                width="1000"
                                height="500"
                                alt={imageData.file.name}
                                src={URL.createObjectURL(imageData.file)}
                            />
                        </Box>
                    )}

                    {!imageData && selectedEvent?.imageUrl && !deleteImage && (
                        <Box className="flex center" mt={1}>
                            <img
                                width="1000"
                                height="500"
                                alt={selectedEvent.imageUrl}
                                src={selectedEvent.imageUrl}
                            />
                        </Box>
                    )}
                    {hasImage && (
                        <Box mt={2} display="flex" justifyContent="center">
                            <Button
                                color="error"
                                variant="outlined"
                                onClick={handleRemoveImage}
                            >
                                Remove image
                            </Button>
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Button
                        className="gucci_text"
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} /> : null}
                    >
                        {loading
                            ? "Saving..."
                            : selectedEvent ? "Update" : "Create"}
                    </Button>
                </Grid>

            </Grid>
            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "300px" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </form>
    );
}
